import * as THREE from './hp-three.module.js';
import { GLTFLoader } from './hp-gltf-loader.js';
import { DRACOLoader } from './hp-draco-loader.js';

class HPCoachingTV extends HTMLElement {
  constructor() {
    super();
    this.clock = new THREE.Clock();
    this.textureLoader = new THREE.TextureLoader();
    this.photoIndex = 0;
    this.photoTextures = [];
    this.standbyTexture = null;
    this.photoTexture = null;
    this.photoMaterial = null;
    this.attributionTexture = null;
    this.attributionMaterial = null;
    this.attributionGeometry = null;
    this.attributionMesh = null;
    this.attributionPivot = null;
    this.drawAttribution = null;
    this.modelLayout = null;
    this.skateboard = null;
    this.skateboardGroup = null;
    this.skateboardLayout = null;
    this.channelMeshes = [];
    this.screenLayers = [];
    this.stageOffsetX = 0;
    this.userRotation = 0;
    this.dragPointerId = null;
    this.dragStartX = 0;
    this.dragStartRotation = 0;
    this.isVisible = true;
    this.raf = null;
    this.flickerRaf = null;
    this.photoTimer = null;
    this.resizeObserver = null;
    this.visibilityObserver = null;
    this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerEnd = this.handlePointerEnd.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.dataset.state = 'loading';

    this.imageUrls = [...new Set(
      [...this.querySelectorAll('[data-tv-image]')]
        .map(node => node.dataset.src)
        .filter(Boolean)
    )].slice(0, 9);
    this.dataset.photoCount = String(this.imageUrls.length);
    this.dataset.rotation = '0';
    this.tabIndex = 0;
    this.setAttribute('role', 'img');
    this.setAttribute('aria-keyshortcuts', 'ArrowLeft ArrowRight Home');
    this.addEventListener('pointerdown', this.handlePointerDown);
    this.addEventListener('pointermove', this.handlePointerMove);
    this.addEventListener('pointerup', this.handlePointerEnd);
    this.addEventListener('pointercancel', this.handlePointerEnd);
    this.addEventListener('keydown', this.handleKeyDown);

    try {
      this.createStage();
      this.loadModel();
    } catch (error) {
      this.dataset.state = 'fallback';
      console.warn('Coaching TV could not be initialized.', error);
    }
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    cancelAnimationFrame(this.flickerRaf);
    clearTimeout(this.photoTimer);
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.removeEventListener('pointerdown', this.handlePointerDown);
    this.removeEventListener('pointermove', this.handlePointerMove);
    this.removeEventListener('pointerup', this.handlePointerEnd);
    this.removeEventListener('pointercancel', this.handlePointerEnd);
    this.removeEventListener('keydown', this.handleKeyDown);
    this.photoTextures.forEach(texture => texture.dispose());
    this.standbyTexture?.dispose();
    this.photoMaterial?.dispose();
    this.attributionTexture?.dispose();
    this.attributionMaterial?.dispose();
    this.attributionGeometry?.dispose();
    this.renderer?.dispose();
  }

  createStage() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(31, 1, 0.01, 100);
    this.camera.position.set(1.25, 2.45, 7.85);
    this.camera.lookAt(0, 0, 0);

    this.renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance'
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.domElement.className = 'hp-coaching__tv-canvas';
    this.renderer.domElement.setAttribute('aria-hidden', 'true');
    this.prepend(this.renderer.domElement);

    this.stage = new THREE.Group();
    this.stage.rotation.y = Math.PI - 0.025;
    this.scene.add(this.stage);

    this.scene.add(new THREE.HemisphereLight(0xfff6df, 0x3852a6, 2.25));

    const keyLight = new THREE.DirectionalLight(0xffffff, 3.2);
    keyLight.position.set(4, 6, 7);
    this.scene.add(keyLight);

    const rimLight = new THREE.DirectionalLight(0x9db0ff, 2.1);
    rimLight.position.set(-5, 2, -4);
    this.scene.add(rimLight);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this);
    this.resize();

    this.visibilityObserver = new IntersectionObserver(entries => {
      this.isVisible = entries[0]?.isIntersecting !== false;
      if (this.isVisible && !this.raf) this.render();
    }, { rootMargin: '160px' });
    this.visibilityObserver.observe(this);
  }

  async loadModel() {
    const modelUrl = this.dataset.model;
    if (!modelUrl) {
      this.dataset.state = 'fallback';
      return;
    }

    try {
      const gltf = await new GLTFLoader().loadAsync(modelUrl);
      this.model = gltf.scene;
      this.stage.add(this.model);
      this.cleanTelevisionGeometry();
      this.hideSceneProps();
      this.applyResponsiveModelLayout();
      await this.loadSkateboard();
      this.createAttributionFloor();

      this.prepareChannelMeshes();

      if (this.channelMeshes.length && this.imageUrls.length) {
        this.showStandbyScreen();
        this.dataset.state = 'ready';
        this.render();

        const firstTexture = await this.loadPhotoTexture(this.imageUrls[0]);
        if (firstTexture) {
          await this.waitForPreloadWindow();
          this.renderer.initTexture?.(firstTexture);
          this.photoTextures = [firstTexture];
          this.dataset.preloadedPhotoCount = '1';
          this.dataset.photoCount = '1';
          this.showPhoto(0, false);
          this.standbyTexture?.dispose();
          this.standbyTexture = null;
        }

        await this.preloadRemainingPhotos();
        if (this.photoTextures.length > 1) this.scheduleNextPhoto();
        return;
      }

      this.dataset.state = 'ready';
      this.render();
    } catch (error) {
      this.dataset.state = 'fallback';
      console.warn('Coaching TV could not be loaded.', error);
    }
  }

  fitModel() {
    const bounds = new THREE.Box3();
    const screenBounds = new THREE.Box3();
    this.model.updateWorldMatrix(true, true);
    const worldToModel = this.model.matrixWorld.clone().invert();

    this.model.traverse(object => {
      if (!object.isMesh) return;
      let ancestor = object;
      while (ancestor && ancestor !== this.model) {
        if (!ancestor.visible) return;
        ancestor = ancestor.parent;
      }
      object.geometry.computeBoundingBox();
      if (!object.geometry.boundingBox) return;

      const meshBounds = object.geometry.boundingBox
        .clone()
        .applyMatrix4(object.matrixWorld)
        .applyMatrix4(worldToModel);
      bounds.union(meshBounds);

      const materials = Array.isArray(object.material) ? object.material : [object.material];
      const screenKey = [object.name, ...materials.map(material => material?.name || '')].join(' ');
      if (/screenchannel1/i.test(screenKey)) screenBounds.union(meshBounds);
    });

    const size = bounds.getSize(new THREE.Vector3());
    const center = bounds.getCenter(new THREE.Vector3());
    const screenCenter = screenBounds.isEmpty()
      ? center
      : screenBounds.getCenter(new THREE.Vector3());
    // The screen is the visual anchor, so the antenna no longer drags the TV
    // below centre. CSS supplies a device-specific model size.
    const targetSize = Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--hp-tv-model-size')
    ) || 3.3;
    const anchorY = Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--hp-tv-anchor-y')
    ) || 0;
    const rotationPadding = Math.max(Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--hp-tv-rotation-padding')
    ) || 1.18, 1);
    const pivotX = center.x;
    const pivotZ = center.z;
    const yawRadius = Math.max(
      Math.hypot(bounds.min.x - pivotX, bounds.min.z - pivotZ),
      Math.hypot(bounds.min.x - pivotX, bounds.max.z - pivotZ),
      Math.hypot(bounds.max.x - pivotX, bounds.min.z - pivotZ),
      Math.hypot(bounds.max.x - pivotX, bounds.max.z - pivotZ)
    );
    // Fit against the widest possible yaw footprint, not only the front-facing
    // box. The fixed padding also covers perspective growth as the rear corners
    // rotate closer to the camera, preventing edge clipping without pulsing the
    // model scale while it is being dragged.
    const rotationSafeSize = Math.max(size.y, yawRadius * 2, 0.001);
    const scale = targetSize / (rotationSafeSize * rotationPadding);

    this.model.scale.setScalar(scale);
    this.model.position.set(
      -center.x * scale,
      -screenCenter.y * scale + anchorY,
      -center.z * scale
    );
    this.modelLayout = {
      minX: bounds.min.x * scale + this.model.position.x,
      maxX: bounds.max.x * scale + this.model.position.x,
      minY: bounds.min.y * scale + this.model.position.y,
      maxY: bounds.max.y * scale + this.model.position.y,
      minZ: bounds.min.z * scale + this.model.position.z,
      maxZ: bounds.max.z * scale + this.model.position.z
    };
    this.currentModelSize = targetSize;
    this.currentAnchorY = anchorY;
    this.currentRotationPadding = rotationPadding;
    this.layoutSkateboard();
    this.updateAttributionFloorLayout();
  }

  async loadSkateboard() {
    const modelUrl = this.dataset.boardModel;
    if (!modelUrl || this.skateboard) return;

    this.dataset.boardState = 'loading';

    try {
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
      const loader = new GLTFLoader().setDRACOLoader(dracoLoader);
      const gltf = await loader.loadAsync(modelUrl);
      dracoLoader.dispose();
      this.skateboard = gltf.scene;
      const bounds = new THREE.Box3().setFromObject(this.skateboard);
      const center = bounds.getCenter(new THREE.Vector3());
      const size = bounds.getSize(new THREE.Vector3());
      this.skateboard.position.sub(center);
      this.skateboardLayout = { size };

      this.skateboardGroup = new THREE.Group();
      this.skateboardGroup.name = 'CoachingSkateboardMount';
      this.skateboardGroup.add(this.skateboard);
      this.stage.add(this.skateboardGroup);

      await this.applySkateboardMaterials();
      this.layoutSkateboard();
      this.dataset.boardState = 'ready';
    } catch (error) {
      this.dataset.boardState = 'fallback';
      console.warn('The coaching skateboard could not be loaded.', error);
    }
  }

  async applySkateboardMaterials() {
    if (!this.skateboard) return;

    const loadTexture = async (url, flipDeck = false) => {
      if (!url) return null;

      try {
        const texture = await this.textureLoader.loadAsync(url);
        texture.flipY = false;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.anisotropy = Math.min(this.renderer.capabilities.getMaxAnisotropy(), 8);

        if (flipDeck) {
          texture.center.set(0.5, 0.5);
          texture.repeat.y = -Math.abs(texture.repeat.y || 1);
        }

        texture.needsUpdate = true;
        return texture;
      } catch (error) {
        console.warn('A coaching skateboard texture could not be loaded.', error);
        return null;
      }
    };

    const [deckTexture, wheelTexture, gripTexture] = await Promise.all([
      loadTexture(this.dataset.boardDeck, true),
      loadTexture(this.dataset.boardWheel),
      loadTexture(this.dataset.boardGrip)
    ]);

    this.skateboard.traverse(object => {
      if (!object.isMesh) return;
      const name = object.name.toLowerCase();

      if (name === 'griptape') {
        object.material = new THREE.MeshStandardMaterial({
          map: gripTexture,
          color: gripTexture ? 0xffffff : 0x4a4a4a,
          roughness: 0.82
        });
      } else if (name === 'deck') {
        object.material = new THREE.MeshStandardMaterial({
          map: deckTexture,
          color: 0xffffff,
          roughness: 0.14,
          side: THREE.DoubleSide
        });
      } else if (name.includes('wheel')) {
        object.material = new THREE.MeshStandardMaterial({
          map: wheelTexture,
          color: 0xffffff,
          roughness: 0.35
        });
      } else if (name.includes('truck') || name.includes('baseplate')) {
        object.material = new THREE.MeshStandardMaterial({
          color: 0xc0c5c9,
          metalness: name.includes('baseplate') ? 0.75 : 0.8,
          roughness: name.includes('baseplate') ? 0.28 : 0.25
        });
      } else if (name.includes('bolt')) {
        object.material = new THREE.MeshStandardMaterial({
          color: this.dataset.boardBolt || 0x111111,
          metalness: 0.5,
          roughness: 0.3
        });
      }
    });
  }

  layoutSkateboard() {
    if (!this.skateboardGroup || !this.skateboardLayout || !this.modelLayout) return;

    const { size } = this.skateboardLayout;
    const televisionWidth = this.modelLayout.maxX - this.modelLayout.minX;
    const targetLength = televisionWidth * 1.72;
    const boardLength = Math.max(size.x, size.z, 0.001);
    const scale = targetLength / boardLength;
    const longAxisIsDepth = size.z > size.x;
    const televisionCenterX = (this.modelLayout.minX + this.modelLayout.maxX) / 2;
    const televisionCenterZ = (this.modelLayout.minZ + this.modelLayout.maxZ) / 2;
    const boardHalfHeight = size.y * scale * 0.5;

    this.skateboardGroup.rotation.set(0, longAxisIsDepth ? Math.PI / 2 : 0, 0);
    this.skateboardGroup.scale.setScalar(scale);
    this.skateboardGroup.position.set(
      televisionCenterX,
      this.modelLayout.minY - boardHalfHeight + 0.11,
      televisionCenterZ + 0.08
    );
  }

  createAttributionFloor() {
    if (this.attributionMesh || !this.dataset.attribution) return;

    const canvas = document.createElement('canvas');
    canvas.width = 1536;
    canvas.height = 360;
    const context = canvas.getContext('2d');
    const drawAttribution = (force = false) => {
      const compactLayout = window.matchMedia('(max-width: 1024px)').matches;
      if (!force && this.attributionCompactLayout === compactLayout) return;
      this.attributionCompactLayout = compactLayout;
      this.dataset.attributionLayout = compactLayout ? 'two-line-compact' : 'two-line';

      context.clearRect(0, 0, canvas.width, canvas.height);
      context.save();
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.shadowColor = 'rgba(9, 9, 9, 0.55)';
      context.shadowBlur = 2;
      context.shadowOffsetY = 3;
      context.fillStyle = 'rgba(247, 245, 239, 0.82)';

      const drawCurvedLine = (text, centerY, bend) => {
        const characters = [...text];
        const widths = characters.map(character => context.measureText(character).width);
        const totalWidth = widths.reduce((sum, width) => sum + width, 0);
        let cursor = -totalWidth / 2;

        characters.forEach((character, index) => {
          const characterWidth = widths[index];
          const offsetX = cursor + characterWidth / 2;
          const normalizedX = offsetX / (totalWidth / 2);
          const offsetY = bend * (1 - normalizedX * normalizedX);
          const slope = (-2 * bend * normalizedX) / (totalWidth / 2);

          context.save();
          context.translate(canvas.width / 2 + offsetX, centerY + offsetY);
          context.rotate(Math.atan(slope));
          context.fillText(character, 0, 0);
          context.restore();
          cursor += characterWidth;
        });
      };

      if (compactLayout) {
        context.font = '500 44px "DM Mono", ui-monospace, monospace';
        drawCurvedLine('3D MODEL: GRANDMA’S', 154, 24);
        drawCurvedLine('TV BY ATSKAHEART · CC BY 4.0', 238, 28);
      } else {
        context.font = '500 56px "DM Mono", ui-monospace, monospace';
        drawCurvedLine('3D MODEL: GRANDMA’S TV', 148, 28);
        drawCurvedLine('BY ATSKAHEART · CC BY 4.0', 246, 34);
      }
      context.restore();
      if (this.attributionTexture) this.attributionTexture.needsUpdate = true;
    };

    this.drawAttribution = drawAttribution;
    drawAttribution(true);
    this.attributionTexture = new THREE.CanvasTexture(canvas);
    this.attributionTexture.colorSpace = THREE.SRGBColorSpace;
    this.attributionTexture.minFilter = THREE.LinearFilter;
    this.attributionTexture.magFilter = THREE.LinearFilter;
    this.attributionTexture.generateMipmaps = false;

    this.attributionGeometry = new THREE.PlaneGeometry(1, 1, 48, 1);
    const positions = this.attributionGeometry.attributes.position;
    for (let index = 0; index < positions.count; index += 1) {
      const normalizedX = positions.getX(index) * 2;
      const inwardCurve = -(1 - normalizedX * normalizedX) * 0.38;
      positions.setY(index, positions.getY(index) + inwardCurve);
    }
    positions.needsUpdate = true;
    this.attributionGeometry.computeBoundingSphere();
    this.attributionMaterial = new THREE.MeshBasicMaterial({
      map: this.attributionTexture,
      transparent: true,
      opacity: 0.86,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false
    });
    this.attributionMesh = new THREE.Mesh(this.attributionGeometry, this.attributionMaterial);
    this.attributionMesh.name = 'CoachingModelAttribution';
    this.attributionMesh.rotation.x = -Math.PI / 2;
    this.attributionMesh.rotation.z = 0;
    this.attributionPivot = new THREE.Group();
    this.attributionPivot.name = 'CoachingModelAttributionPivot';
    this.attributionPivot.add(this.attributionMesh);
    this.stage.add(this.attributionPivot);
    this.updateAttributionFloorLayout();

    document.fonts?.ready.then(() => drawAttribution(true));
  }

  updateAttributionFloorLayout() {
    if (!this.attributionMesh || !this.modelLayout) return;

    const width = Math.max(
      (this.modelLayout.maxX - this.modelLayout.minX) * 1.2,
      this.currentModelSize * 0.95
    );
    const depth = width * (360 / 1536);
    const rearEdge = this.modelLayout.maxZ;
    const compactLayout = window.matchMedia('(max-width: 1024px)').matches;
    const rearOffset = compactLayout ? -0.52 : -0.18;
    const modelCenterX = (this.modelLayout.minX + this.modelLayout.maxX) / 2;
    const horizontalOffset = compactLayout ? width * -0.03 : 0;

    this.attributionMesh.scale.set(width, depth, 1);
    this.attributionMesh.position.set(0, 0, 0);
    this.attributionPivot.position.set(
      modelCenterX + horizontalOffset,
      this.modelLayout.minY + 0.018,
      rearEdge + depth * rearOffset
    );
    this.attributionPivot.rotation.y = compactLayout
      ? THREE.MathUtils.degToRad(-4)
      : 0;
  }

  hideSceneProps() {
    let hiddenPropCount = 0;

    this.model.traverse(object => {
      if (!/^(?:TV_Table|RemoteControl|VHS_Reader|shadows)$/i.test(object.name)) return;
      object.visible = false;
      hiddenPropCount += 1;
    });

    this.dataset.hiddenPropCount = String(hiddenPropCount);
  }

  cleanTelevisionGeometry() {
    let removedTriangleCount = 0;
    let removedFabricTriangleCount = 0;
    let removedFrontTapeTriangleCount = 0;
    let removedCableTriangleCount = 0;

    this.model.traverse(object => {
      if (!object.isMesh) return;

      const isTelevisionMesh = /^TV_Tv(?:_|$)/i.test(object.name);
      // GLTFLoader sanitizes the source `VHS_Reader.001` name to `VHS_Reader001`.
      const isTapeMesh = /^VHS_Reader(?:\.?001)_Tv(?:_|$)/i.test(object.name);
      if (!isTelevisionMesh && !isTapeMesh) return;

      const sourceGeometry = object.geometry;
      const index = sourceGeometry.getIndex();
      const uv = sourceGeometry.getAttribute('uv');
      if (!index || !uv) return;

      const keptIndices = [];
      let meshRemovedTriangleCount = 0;

      for (let offset = 0; offset < index.count; offset += 3) {
        const triangleIndex = offset / 3;
        const a = index.getX(offset);
        const b = index.getX(offset + 1);
        const c = index.getX(offset + 2);
        const vertices = [a, b, c];
        // The downloaded model maps its lace cloth to this isolated atlas region.
        const isFabricTriangle = isTelevisionMesh && vertices.every(vertex => {
          const u = uv.getX(vertex);
          const v = uv.getY(vertex);
          return u >= 0.4 && u <= 0.59 && v >= 0.84;
        });
        // The first 36 tape triangles are the stack beside the TV; keep those intact.
        const isFrontTapeTriangle = isTapeMesh && triangleIndex >= 36 && triangleIndex <= 59;
        const isCableTriangle = isTelevisionMesh && ((triangleIndex >= 258 && triangleIndex <= 259)
          || (triangleIndex >= 308 && triangleIndex <= 325)
          || (triangleIndex >= 344 && triangleIndex <= 345));

        if (isFabricTriangle || isFrontTapeTriangle || isCableTriangle) {
          meshRemovedTriangleCount += 1;
          removedTriangleCount += 1;
          if (isFabricTriangle) removedFabricTriangleCount += 1;
          if (isFrontTapeTriangle) removedFrontTapeTriangleCount += 1;
          if (isCableTriangle) removedCableTriangleCount += 1;
          continue;
        }

        keptIndices.push(a, b, c);
      }

      if (!meshRemovedTriangleCount) return;

      const filteredGeometry = sourceGeometry.clone();
      filteredGeometry.setIndex(keptIndices);
      // Compact the attributes so removed props no longer affect model fitting.
      const geometry = filteredGeometry.toNonIndexed();
      filteredGeometry.dispose();
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      object.geometry = geometry;
    });

    this.dataset.cleanedTriangleCount = String(removedTriangleCount);
    this.dataset.fabricTrianglesRemoved = String(removedFabricTriangleCount);
    this.dataset.frontTapeTrianglesRemoved = String(removedFrontTapeTriangleCount);
    this.dataset.cableTrianglesRemoved = String(removedCableTriangleCount);
  }

  prepareChannelMeshes() {
    this.model.traverse(object => {
      if (object.isMesh) return;
      if (!/^TV_(?:screennoise|screenempty|screennosignal|screenchannel|standby)(?:_|$)/i.test(object.name)) return;

      object.visible = false;
      this.screenLayers.push(object);
    });

    this.model.traverse(object => {
      if (!object.isMesh) return;

      const materials = Array.isArray(object.material) ? object.material : [object.material];
      const screenKey = [object.name, ...materials.map(material => material?.name || '')].join(' ');
      if (!/screenchannel1/i.test(screenKey)) return;

      object.geometry = object.geometry.clone();
      this.normalizeUvs(object.geometry);
      object.renderOrder = 4;
      object.parent.visible = true;
      object.parent.position.z = -73.45;
      this.channelMeshes.push(object);
    });

    this.dataset.channelCount = String(this.channelMeshes.length);
  }

  normalizeUvs(geometry) {
    const uv = geometry.getAttribute('uv');
    if (!uv) return;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (let i = 0; i < uv.count; i += 1) {
      minX = Math.min(minX, uv.getX(i));
      minY = Math.min(minY, uv.getY(i));
      maxX = Math.max(maxX, uv.getX(i));
      maxY = Math.max(maxY, uv.getY(i));
    }

    const width = Math.max(maxX - minX, 0.0001);
    const height = Math.max(maxY - minY, 0.0001);

    for (let i = 0; i < uv.count; i += 1) {
      uv.setXY(i, (uv.getX(i) - minX) / width, (uv.getY(i) - minY) / height);
    }

    uv.needsUpdate = true;
  }

  showStandbyScreen() {
    const size = 16;
    const data = new Uint8Array(size * size * 4);

    for (let index = 0; index < size * size; index += 1) {
      const noise = 52 + ((index * 47 + (index % size) * 19) % 76);
      const offset = index * 4;
      data[offset] = noise;
      data[offset + 1] = noise + 5;
      data[offset + 2] = noise + 2;
      data[offset + 3] = 255;
    }

    this.standbyTexture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
    this.standbyTexture.colorSpace = THREE.SRGBColorSpace;
    this.standbyTexture.wrapS = this.standbyTexture.wrapT = THREE.RepeatWrapping;
    this.standbyTexture.repeat.set(12, 9);
    this.standbyTexture.magFilter = THREE.NearestFilter;
    this.standbyTexture.minFilter = THREE.NearestFilter;
    this.standbyTexture.needsUpdate = true;

    this.photoMaterial = this.createRetroScreenMaterial(this.standbyTexture);
    this.channelMeshes.forEach(mesh => {
      mesh.material = this.photoMaterial;
    });
    this.dataset.screenState = 'standby';
    this.dataset.transitioning = 'false';
  }

  async loadPhotoTexture(url) {
    try {
      const texture = await this.textureLoader.loadAsync(url);
      this.configureTexture(texture);
      return texture;
    } catch (error) {
      console.warn('A coaching TV image could not be preloaded.', error);
      return null;
    }
  }

  async preloadRemainingPhotos() {
    this.dataset.preloading = 'true';

    for (const url of this.imageUrls.slice(1)) {
      if (!this.isConnected) break;
      await this.waitForPreloadWindow();
      const texture = await this.loadPhotoTexture(url);
      if (!texture) continue;

      await this.waitForPreloadWindow();
      this.renderer.initTexture?.(texture);
      this.photoTextures.push(texture);
      this.dataset.preloadedPhotoCount = String(this.photoTextures.length);
      this.dataset.photoCount = String(this.photoTextures.length);
    }

    this.dataset.preloading = 'false';
  }

  waitForPreloadWindow() {
    return new Promise(resolve => {
      const attempt = () => {
        if (!this.isConnected) {
          resolve();
          return;
        }

        if (this.dragPointerId !== null) {
          setTimeout(attempt, 120);
          return;
        }

        const finish = () => {
          if (this.dragPointerId !== null) {
            setTimeout(attempt, 120);
          } else {
            resolve();
          }
        };

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(finish, { timeout: 650 });
        } else {
          setTimeout(finish, 90);
        }
      };

      attempt();
    });
  }

  showPhoto(index, transition = true) {
    if (!this.photoTextures.length) return;

    const nextIndex = (index + this.photoTextures.length) % this.photoTextures.length;
    const texture = this.photoTextures[nextIndex];

    if (!this.photoMaterial) {
      this.photoMaterial = this.createRetroScreenMaterial(texture);
      this.channelMeshes.forEach(mesh => {
        mesh.material = this.photoMaterial;
      });
      this.dataset.transitioning = 'false';
    } else {
      this.photoMaterial.uniforms.uMap.value = texture;
      if (transition) this.animateScreenFlicker();
    }

    this.photoTexture = texture;
    this.photoIndex = nextIndex;
    this.dataset.photoIndex = String(nextIndex);
    this.dataset.screenState = 'photo';
  }

  createRetroScreenMaterial(texture) {
    return new THREE.ShaderMaterial({
      uniforms: {
        uMap: { value: texture },
        uTime: { value: 0 },
        uSwitchPulse: { value: 0 }
      },
      vertexShader: `
        varying vec2 vUv;

        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;

        uniform sampler2D uMap;
        uniform float uTime;
        uniform float uSwitchPulse;
        varying vec2 vUv;

        float randomNoise(vec2 point) {
          return fract(sin(dot(point, vec2(12.9898, 78.233))) * 43758.5453);
        }

        void main() {
          vec2 uv = vUv;
          float chroma = 0.0015 + (uSwitchPulse * 0.0035);
          vec3 colour = vec3(
            texture2D(uMap, uv + vec2(chroma, 0.0)).r,
            texture2D(uMap, uv).g,
            texture2D(uMap, uv - vec2(chroma, 0.0)).b
          );
          colour = pow(max(colour, vec3(0.0)), vec3(0.4545));

          float scanline = 0.945 + 0.055 * sin((uv.y * 560.0) + (uTime * 4.0));
          float grain = randomNoise((uv * vec2(420.0, 300.0)) + floor(uTime * 22.0));
          float vignetteShape = 16.0 * uv.x * uv.y * (1.0 - uv.x) * (1.0 - uv.y);
          float vignette = mix(0.82, 1.0, pow(clamp(vignetteShape, 0.0, 1.0), 0.18));
          float switchBand = step(0.72, fract((uv.y * 4.0) + (uTime * 27.0)));

          colour *= scanline * vignette;
          colour += (grain - 0.5) * (0.035 + (uSwitchPulse * 0.055));
          colour += vec3(0.10, 0.13, 0.09) * uSwitchPulse * switchBand;
          colour *= vec3(0.97, 1.01, 0.96);

          gl_FragColor = vec4(clamp(colour, 0.0, 1.0), 1.0);
        }
      `,
      side: THREE.DoubleSide,
      toneMapped: false,
      transparent: false
    });
  }

  animateScreenFlicker() {
    const uniform = this.photoMaterial?.uniforms?.uSwitchPulse;
    if (!uniform || this.reducedMotion) {
      if (uniform) uniform.value = 0;
      this.dataset.transitioning = 'false';
      return;
    }

    cancelAnimationFrame(this.flickerRaf);
    this.dataset.transitioning = 'true';
    const startTime = performance.now();
    const duration = 145;

    const step = now => {
      const progress = Math.min((now - startTime) / duration, 1);
      const stutter = 0.55 + (0.45 * Math.abs(Math.sin(progress * Math.PI * 6)));
      uniform.value = (1 - progress) * stutter;

      if (progress < 1 && this.isConnected) {
        this.flickerRaf = requestAnimationFrame(step);
      } else {
        uniform.value = 0;
        this.dataset.transitioning = 'false';
      }
    };

    this.flickerRaf = requestAnimationFrame(step);
  }

  configureTexture(texture) {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.flipY = true;
    texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.anisotropy = Math.min(this.renderer.capabilities.getMaxAnisotropy(), 8);
    this.cropTextureToScreen(texture);
  }

  cropTextureToScreen(texture) {
    const imageWidth = texture.image?.naturalWidth || texture.image?.width || 4;
    const imageHeight = texture.image?.naturalHeight || texture.image?.height || 3;
    const imageAspect = imageWidth / Math.max(imageHeight, 1);
    const screenAspect = 1.22;

    texture.repeat.set(1, 1);
    texture.offset.set(0, 0);

    if (imageAspect > screenAspect) {
      texture.repeat.x = screenAspect / imageAspect;
      texture.offset.x = (1 - texture.repeat.x) / 2;
    } else {
      texture.repeat.y = imageAspect / screenAspect;
      texture.offset.y = (1 - texture.repeat.y) / 2;
    }

    texture.needsUpdate = true;
  }

  scheduleNextPhoto() {
    clearTimeout(this.photoTimer);
    if (this.reducedMotion || this.imageUrls.length < 2) return;

    const interval = Math.max(Number(this.dataset.interval) || 2500, 2000);
    this.photoTimer = setTimeout(() => {
      this.showPhoto(this.photoIndex + 1, true);
      this.scheduleNextPhoto();
    }, interval);
  }

  handlePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartRotation = this.userRotation;
    this.dataset.dragging = 'true';
    this.setPointerCapture?.(event.pointerId);
  }

  handlePointerMove(event) {
    if (event.pointerId !== this.dragPointerId) return;

    const width = Math.max(this.clientWidth, 1);
    const delta = (event.clientX - this.dragStartX) / width;
    this.userRotation = this.dragStartRotation + delta * Math.PI * 2;
    this.updateRotationAttribute();
  }

  handlePointerEnd(event) {
    if (event.pointerId !== this.dragPointerId) return;

    if (this.hasPointerCapture?.(event.pointerId)) {
      this.releasePointerCapture(event.pointerId);
    }
    this.dragPointerId = null;
    this.dataset.dragging = 'false';
  }

  handleKeyDown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home'].includes(event.key)) return;
    event.preventDefault();

    if (event.key === 'Home') {
      this.userRotation = 0;
    } else {
      this.userRotation += event.key === 'ArrowLeft' ? -Math.PI / 12 : Math.PI / 12;
    }

    this.updateRotationAttribute();
  }

  updateRotationAttribute() {
    const degrees = ((THREE.MathUtils.radToDeg(this.userRotation) % 360) + 360) % 360;
    this.dataset.rotation = degrees.toFixed(1);
  }

  resize() {
    if (!this.renderer || !this.camera) return;
    const width = Math.max(this.clientWidth, 1);
    const height = Math.max(this.clientHeight, 1);
    this.stageOffsetX = Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--hp-tv-offset-x')
    ) || 0;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    this.renderer.setSize(width, height, false);

    if (this.model) this.applyResponsiveModelLayout();
    this.drawAttribution?.();
  }

  applyResponsiveModelLayout() {
    const hideTapes = window.matchMedia('(max-width: 1024px)').matches;
    let tapeVisibilityChanged = false;

    this.model.traverse(object => {
      if (!/^VHS_Reader(?:\.?001)_Tv(?:_|$)/i.test(object.name)) return;
      const nextVisibility = !hideTapes;
      if (object.visible !== nextVisibility) tapeVisibilityChanged = true;
      object.visible = nextVisibility;
    });

    const nextModelSize = Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--hp-tv-model-size')
    ) || 3.3;
    const nextAnchorY = Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--hp-tv-anchor-y')
    ) || 0;
    const nextRotationPadding = Math.max(Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--hp-tv-rotation-padding')
    ) || 1.18, 1);
    this.dataset.tapesVisible = String(!hideTapes);

    if (tapeVisibilityChanged
      || this.currentModelSize !== nextModelSize
      || this.currentAnchorY !== nextAnchorY
      || this.currentRotationPadding !== nextRotationPadding) {
      this.fitModel();
    }
  }

  render = () => {
    this.raf = null;
    if (!this.renderer || !this.scene || !this.camera || !this.isVisible) return;

    this.stage.position.x = this.stageOffsetX;
    this.stage.position.y = 0;
    this.stage.rotation.y = Math.PI - 0.025 + this.userRotation;
    if (!this.reducedMotion && this.photoMaterial?.uniforms?.uTime) {
      this.photoMaterial.uniforms.uTime.value = performance.now() * 0.001;
    }

    this.renderer.render(this.scene, this.camera);
    this.raf = requestAnimationFrame(this.render);
  };
}

if (!customElements.get('hp-coaching-tv')) {
  customElements.define('hp-coaching-tv', HPCoachingTV);
}
